/* prettier-ignore */

import { computed, inject, Injectable, NgZone } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { filter, groupBy, GroupedObservable, ignoreElements, map, merge, Observable, tap,
   retry, shareReplay, Subject, Subscription, take, takeUntil, timer, mergeMap
} from 'rxjs';

import { DebugService } from 'src/app/services/debug.service';
import { RestService } from 'src/app/services/rest.service';
import { environment } from 'src/environments/environment';
import { ChatBotTextAnimationService } from './textAnimationService.service';
import { PlayAudioHandlerService } from './playAudioHandler.service';
import { DataChatService } from './dataChat.service';
import { v4 as uuidv4 } from 'uuid';

const MAX_TRACKED_IDS = 50;

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private readonly ngZone = inject(NgZone);
  private readonly rest = inject(RestService);
  private readonly dataChat = inject(DataChatService);
  private readonly playService = inject(PlayAudioHandlerService);
  private readonly debugService = inject(DebugService);
  private readonly textAnimation = inject(ChatBotTextAnimationService);

  private readonly urlWebSocket = computed(() => `${environment.urlWebSocket}/${this.dataChat.token()}/${this.dataChat.id()}`);

  private wsSend!: WebSocketSubject<any>;
  private wsRecv$!: Observable<any>;
  private wsSub?: Subscription;

  private readonly cancelled = new Set<string>();
  private cancel$ = new Subject<string>();
  private disconnect$ = new Subject<void>();

  private connected = false;
  private activeCompletionId: string | null = null;
  private readonly completionIds = new Set<string>();

// ──────────────────────── Connection lifecycle ────────────────────────
  connectAll(): void {
    if (this.connected) {
      this.debugService.warn('Already active WebSocket connection', '[WS]');
      return;
    }

    this.debugService.info('Connecting WebSocket…', '[WS]');
    const { subject, recv$ } = this.createSocket(this.urlWebSocket());
    this.wsSend = subject;
    this.wsRecv$ = recv$;

    this.ngZone.runOutsideAngular(() => {
      this.wsSub = this.buildPipeline()
        .pipe(takeUntil(this.disconnect$))
        .subscribe();
    });

    this.connected = true;
  }

  disconnect(): void {
    this.disconnect$.next();

    this.wsSub?.unsubscribe();
    this.wsSub = undefined;

    try { this.wsSend?.complete(); } catch {}
    this.wsSend = undefined!;

    try { this.cancel$.complete(); } catch {}
    this.cancelled.clear();
    this.completionIds.clear();

    this.disconnect$.complete();
    this.cancel$ = new Subject<string>();
    this.disconnect$ = new Subject<void>();
    this.connected = false;
    this.activeCompletionId = null;

    this.debugService.warn('WebSocket disconnected', '[WS]');
  }

// ──────────────────────── Socket factory ────────────────────────
  private createSocket(url: string) {
    const subject = webSocket<any>({
      url,
      serializer: JSON.stringify,
      deserializer: (e) => {
        try {
          return e.data && e.data !== 'undefined' 
            ? JSON.parse(e.data) 
            : null;
        } catch {
          return null;
        }
      },
      openObserver:  { next: () => this.debugService.info(url, '[WS] OPEN') },
      closeObserver: { next: () => this.debugService.info(url, '[WS] CLOSE') },
    });

    const recv$ = subject.pipe(
      retry({
        delay: (_, count) => {
          const backoff = Math.min(30_000, 1_000 * 2 ** count);
          return timer(backoff + Math.random() * 1_000);
        },
        resetOnSuccess: true,
      }),
      takeUntil(this.disconnect$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    return { subject, recv$ };
  }

// ──────────────────────── Stream pipeline ────────────────────────
  private buildPipeline(): Observable<unknown> {
    return this.wsRecv$.pipe(
      filter((e) => e != null),
      tap((e) => {
        console.log("CHUNKS ", e)
        this.debugService.info(e, '[WS] chunk');
        if (!this.isTracked(e.completion_id))
          this.debugService.warn(e.completion_id, '[WS] untracked completion_id');
      }),
      filter((m) => this.isTracked(m.completion_id)),
      groupBy(
        (m) => m.completion_id,
        {
          duration: (grp) => merge(
            grp.pipe(filter((m) => m.event_type === 'final' || m.event_type === 'end'), take(1)),
            grp.pipe(filter((m) => m.event_type === 'error'), take(1)),
            this.cancel$.pipe(filter((id) => id === grp.key || id === this.baseId(grp.key)), take(1)),
            this.disconnect$.pipe(take(1)),
          ),
        },
      ),
      mergeMap((group$) => this.processGroup(group$)),
    );
  }

  private processGroup(group$: GroupedObservable<string, any>): Observable<never> {
    const id = group$.key;

    const textChunk$ = group$.pipe(
      filter((m) => m.event_type === 'chunk' && !!m.content?.text_content),
      map((m) => ({
        text: m.content.text_content as string,
        hiddenCommand: m.content.hidden_command as string | undefined,
      })),
    );

    const final$ = group$.pipe(filter((m) => m.event_type === 'final' || m.event_type === 'end'), take(1));
    const error$ = group$.pipe(filter((m) => m.event_type === 'error'), take(1));
    const cancelForId$ = this.cancel$.pipe(filter((x) => x === id), take(1));
    const stop$ = merge(final$, error$, cancelForId$, this.disconnect$).pipe(take(1));

    const textFlow$ = textChunk$.pipe(
      takeUntil(stop$),
      tap((chunk: { text: string; hiddenCommand?: string }) => {
        if (this.isCancelled(id)) 
          return;

        this.disableLoader();
        this.textAnimation.enqueueText(id, chunk.text, chunk.hiddenCommand);
      }),
      ignoreElements(),
    );

    const onError$ = error$.pipe(
      tap(() => this.ngZone.run(() => this.showErrorBubble())),
      ignoreElements(),
    );

    return merge(textFlow$, onError$);
  }

// ──────────────────────── Public API ────────────────────────
  public trackCompletionId(id: string, _type: 'text' | 'audio' = 'text'): void {
    this.pruneTrackedIds();
    this.completionIds.add(id);
    this.activeCompletionId = id;
  }

  public async sendWSChatBotData(item: { content: string }, data: string | null = null): Promise<void> {
    await this.cancelCompletion();

    this.dataChat.removeErrorMsgs();
    this.dataChat.hasHistory.set(true);
    this.enableLoader();

    const payload = {
      completion_id: uuidv4(),
      type: 'user_request',
      output_type: 'text',
      content: item.content,
      subdomain: this.rest.subDomain,
      app: this.rest.subDomain,
      route_token: undefined,
      is_chunk: false,
      is_final: true,
      route_active: false,
    };

    this.debugService.info(payload, '[WS] sending payload');
    this.sendPayload(payload);
  }

  public async cancelCompletion(): Promise<void> {
    this.disableLoader();
    await this.playService.stopNow();

    const id = this.activeCompletionId;
    if (!id || this.cancelled.has(id)) 
      return;

    this.cancelled.add(id);
    this.cancel$.next(id);
    try { this.textAnimation.cancel(id); } catch {}
  }

// ──────────────────────── Internals ────────────────────────
  private sendPayload(payload: { completion_id: string; [k: string]: any }): void {
    this.activeCompletionId = payload.completion_id;
    try {
      this.wsSend.next(payload);
      this.completionIds.add(payload.completion_id);
    } catch {
      this.showErrorBubble();
    }
  }

  private isCancelled(id: string): boolean {
    return this.cancelled.has(id) || this.cancelled.has(this.baseId(id));
  }

  private isTracked(id: string): boolean {
    return this.completionIds.has(id) || this.completionIds.has(this.baseId(id));
  }

  private baseId(id: string): string {
    return this.dataChat.allowProactiveBubbles() 
      ? id?.split('_')[0] ?? id
      : 'now-allowed';
  }

  private pruneTrackedIds(): void {
    if (this.completionIds.size > MAX_TRACKED_IDS) {
      const iter = this.completionIds.values();
      for (let i = 0, half = this.completionIds.size >> 1; i < half; i++) {
        const v = iter.next().value;
        if (v) this.completionIds.delete(v);
      }
    }

    if (this.cancelled.size > MAX_TRACKED_IDS) 
      this.cancelled.clear();
  }

  public disableLoader(): void {
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsTyping.set(false);
  }

  public enableLoader(): void {
    this.dataChat.isStreaming.set(true);
    this.dataChat.chatIsTyping.set(true);
  }

  private showErrorBubble(): void {
    this.dataChat.showConnectionErrorMessage();
    this.disableLoader();
  }

}